import React, { useState } from "react";
import { ArrowRight } from "lucide-react";

/**
 * PremiumWorkerForm — shared submission form used by:
 *   • Worker self-apply (/become-premium)
 *   • Agency upload    (/agencies/upload)
 *
 * Caller passes `mode` ("self" | "agency") + `onSubmit(payload)`.
 */
export default function PremiumWorkerForm({ mode, onSubmit, submitting, initial = {} }) {
  const isAgency = mode === "agency";
  const [form, setForm] = useState({
    full_name:        initial.full_name || "",
    email:            initial.email || "",
    phone:            initial.phone || "",
    country:          initial.country || "Saudi Arabia",
    city:             initial.city || "",
    headline:         initial.headline || "",
    role:             initial.role || "",
    experience_years: initial.experience_years || 0,
    skills:           (initial.skills || []).join(", "),
    languages:        (initial.languages || []).join(", "),
    expected_salary_usd: initial.expected_salary_usd || 0,
    cv_text:          initial.cv_text || "",
    cv_url:           initial.cv_url || "",
    note:             initial.note || "",
    reference:        initial.reference || "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      experience_years:    parseInt(form.experience_years || 0, 10),
      expected_salary_usd: parseInt(form.expected_salary_usd || 0, 10),
      skills:    form.skills.split(",").map((s) => s.trim()).filter(Boolean),
      languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
    };
    onSubmit(payload);
  };

  const inputCls = "w-full bg-[#141416] border border-white/15 px-4 py-3 text-white focus:border-white focus:outline-none transition-colors";
  const labelCls = "font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1.5 block";

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid={`premium-form-${mode}`}>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Full name *</label>
          <input required value={form.full_name} onChange={set("full_name")} className={inputCls} data-testid="pf-full-name" />
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="pf-email" />
        </div>
        <div>
          <label className={labelCls}>Phone</label>
          <input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+966 ..." data-testid="pf-phone" />
        </div>
        <div>
          <label className={labelCls}>Country *</label>
          <input required value={form.country} onChange={set("country")} className={inputCls} data-testid="pf-country" />
        </div>
        <div>
          <label className={labelCls}>City</label>
          <input value={form.city} onChange={set("city")} className={inputCls} data-testid="pf-city" />
        </div>
        <div>
          <label className={labelCls}>Role / Title *</label>
          <input required value={form.role} onChange={set("role")} className={inputCls} placeholder="Backend Engineer, Nurse, Welder…" data-testid="pf-role" />
        </div>
      </div>

      <div>
        <label className={labelCls}>One-line headline *</label>
        <input required value={form.headline} onChange={set("headline")} className={inputCls} placeholder="Senior backend engineer with 7y AWS · Riyadh" data-testid="pf-headline" />
      </div>

      <div className="grid sm:grid-cols-3 gap-5">
        <div>
          <label className={labelCls}>Years of experience *</label>
          <input required type="number" min="0" max="60" value={form.experience_years} onChange={set("experience_years")} className={inputCls} data-testid="pf-exp" />
        </div>
        <div>
          <label className={labelCls}>Expected salary (USD/month)</label>
          <input type="number" min="0" value={form.expected_salary_usd} onChange={set("expected_salary_usd")} className={inputCls} data-testid="pf-salary" />
        </div>
        <div>
          <label className={labelCls}>Languages</label>
          <input value={form.languages} onChange={set("languages")} className={inputCls} placeholder="English, Arabic" data-testid="pf-langs" />
        </div>
      </div>

      <div>
        <label className={labelCls}>Skills (comma-separated)</label>
        <input value={form.skills} onChange={set("skills")} className={inputCls} placeholder="Python, FastAPI, AWS, Kubernetes" data-testid="pf-skills" />
      </div>

      <div>
        <label className={labelCls}>CV / Resume (paste plain text or markdown)</label>
        <textarea
          rows={8}
          value={form.cv_text}
          onChange={set("cv_text")}
          className={`${inputCls} font-mono text-sm leading-relaxed`}
          placeholder="Paste the full CV here, or leave blank and add a link below."
          data-testid="pf-cv-text"
        />
      </div>

      <div>
        <label className={labelCls}>OR — link to CV (Drive / Dropbox / PDF URL)</label>
        <input value={form.cv_url} onChange={set("cv_url")} className={inputCls} placeholder="https://…" data-testid="pf-cv-url" />
      </div>

      {isAgency && (
        <div className="border border-[#FF9500]/40 bg-[#FF9500]/[0.05] p-5">
          <label className={`${labelCls} text-[#FF9500]`}>Agency reference / proof *</label>
          <textarea
            required
            rows={3}
            value={form.reference}
            onChange={set("reference")}
            className={`${inputCls} bg-transparent`}
            placeholder="How long has this worker been with you? Any references, certifications, deployed roles? This protects the Premium brand."
            data-testid="pf-reference"
          />
        </div>
      )}

      <div>
        <label className={labelCls}>Anything else the review team should know?</label>
        <textarea rows={3} value={form.note} onChange={set("note")} className={inputCls} data-testid="pf-note" />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-white text-black hover:bg-white/90 disabled:opacity-50 font-bold px-8 py-4 flex items-center justify-center gap-3 transition-colors"
        data-testid="pf-submit"
      >
        {submitting ? "Submitting…" : (isAgency ? "Upload to Premium" : "Submit for review")}
        <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-xs text-white/40 text-center">
        24-hour staff review. We may call to verify. By submitting, you confirm the information is accurate.
      </p>
    </form>
  );
}
